import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config"
import { COMMON_ONE_VERIFY_DEMO_CONFIG } from "@common-shared/config/oneVerify/oneVerifyDemo.config"
import { OneVerifyRouteHelper } from "@common-shared/helpers/oneVerifyRoute.helper"
import type {
    IOneVerifyDemoDiscoverPolicyDefinition,
    IOneVerifyDemoGeneralPolicyDefinition,
    IOneVerifyDemoMedicaidPolicyDefinition,
    IOneVerifyDemoPolicyDefinition,
    IOneVerifyDemoSmartScanPolicyDefinition,
} from "@common-shared/types/oneVerifyDemo.types"
import type { IPayer } from "@common-shared/types/responseData/commonResponseData.types"

import { ToastrHelper } from "@front-shared/helpers/toastr.helper"
import { LookupService } from "@front-shared/services/lookup.service"

import type { IDynamicCheckerFormInputData } from "../types/data.types"
import { LookupHelper } from "./lookup.helper"
import { UtilHelper } from "./util.helper"

export class DemoHelper {
    /**
     * Filters demo policies by label / short / long description for the picker search.
     */
    static filterPolicies(
        policies: IOneVerifyDemoPolicyDefinition[],
        search?: string,
    ): IOneVerifyDemoPolicyDefinition[] {
        const query = search?.trim().toLowerCase() ?? ""

        if (!query) {
            return policies
        }

        return policies.filter(
            policy =>
                policy.label.toLowerCase().includes(query) ||
                policy.shortDescription.toLowerCase().includes(query) ||
                policy.longDescription.toLowerCase().includes(query),
        )
    }

    /**
     * Converts a demo policy to form input data for the read-only checker.
     * Resolves fixture payer external ids to real payers so the form can be
     * submitted as a normal coverage request.
     */
    static async toFormInputData(
        policy: IOneVerifyDemoPolicyDefinition,
    ): Promise<IDynamicCheckerFormInputData | null> {
        switch (policy.checkType) {
            case QuotaUsageType.GENERAL:
                return DemoHelper.toGeneralFormInputData(policy)

            case QuotaUsageType.DISCOVER:
                return DemoHelper.toDiscoverFormInputData(policy)

            case QuotaUsageType.SMART_SCAN:
                return DemoHelper.toSmartScanFormInputData(policy)

            case QuotaUsageType.MEDICAID:
                return DemoHelper.toMedicaidFormInputData(policy)
        }
    }

    /**
     * Feature-specific toast for a blocked demo mutation.
     */
    static getBlockedMutationMessage(pathOrUrl: string): string {
        const normalizedRoute = OneVerifyRouteHelper.normalizeRoute(pathOrUrl)

        for (const entry of COMMON_ONE_VERIFY_DEMO_CONFIG.blockedMutationRouteMessages) {
            const { prefix, message } = entry
            const matches =
                normalizedRoute === prefix ||
                normalizedRoute.startsWith(
                    prefix.endsWith("/") ? prefix : `${prefix}/`,
                )

            if (matches) {
                return message
            }
        }

        return COMMON_ONE_VERIFY_DEMO_CONFIG.defaultBlockedMutationMessage
    }

    /**
     * Resolve a fixture's external payer id to a real payer for form submit.
     */
    private static async resolvePayer(
        payerExternalId: string,
        payerName?: string,
    ): Promise<IPayer | null> {
        const payers = await LookupService.lookupPayers(
            payerExternalId,
            undefined,
            undefined,
            undefined,
            {
                weight: "DESC",
                name: "ASC",
            },
        )
        const payer = payers?.find(item => item.payerId === payerExternalId)

        if (!payer) {
            ToastrHelper.error(
                `Demo payer ${payerName || payerExternalId} was not found.`,
            )

            return null
        }

        return payer
    }

    /**
     * Converts a demo general policy to form input data for the read-only checker.
     */
    private static async toGeneralFormInputData(
        policy: IOneVerifyDemoGeneralPolicyDefinition,
    ): Promise<IDynamicCheckerFormInputData | null> {
        const { coverageRequest } = policy
        const payer = await DemoHelper.resolvePayer(
            coverageRequest.payerExternalId,
            coverageRequest.payerName,
        )

        if (!payer) {
            return null
        }

        const inNetwork = coverageRequest.inNetwork

        return {
            id: UtilHelper.generateUniqueVarchar(100),
            modalityCode: coverageRequest.modalityCode,
            firstName: coverageRequest.firstName,
            lastName: coverageRequest.lastName,
            dob: coverageRequest.dob,
            providerNpi: coverageRequest.providerNpi,
            payer,
            memberId: coverageRequest.memberId,
            sessionRate: String(coverageRequest.sessionCharge / 100),
            inNetworkCheck: inNetwork,
            outNetworkCheck: !inNetwork,
            cptCode: coverageRequest.cptCode,
        }
    }

    /**
     * Converts a demo discover policy to form input data for the read-only checker.
     */
    private static async toDiscoverFormInputData(
        policy: IOneVerifyDemoDiscoverPolicyDefinition,
    ): Promise<IDynamicCheckerFormInputData | null> {
        const { coverageRequest } = policy
        const payer = await DemoHelper.resolvePayer(
            coverageRequest.payerExternalId,
            coverageRequest.payerName,
        )

        if (!payer) {
            return null
        }

        const inNetwork = coverageRequest.inNetwork ?? true

        return {
            id: UtilHelper.generateUniqueVarchar(100),
            modalityCode: coverageRequest.modalityCode,
            firstName: coverageRequest.firstName,
            lastName: coverageRequest.lastName,
            dob: coverageRequest.dob,
            providerNpi: coverageRequest.providerNpi,
            payer,
            zipCode: coverageRequest.zipCode,
            memberIdNotAvailable: true,
            sessionRate: String(coverageRequest.sessionCharge / 100),
            inNetworkCheck: inNetwork,
            outNetworkCheck: !inNetwork,
            cptCode: coverageRequest.cptCode,
        }
    }

    /**
     * Converts a demo smart-scan policy to form input data for the read-only checker.
     */
    private static async toSmartScanFormInputData(
        policy: IOneVerifyDemoSmartScanPolicyDefinition,
    ): Promise<IDynamicCheckerFormInputData> {
        const { coverageRequest } = policy

        const inNetwork = coverageRequest.inNetwork ?? true

        return {
            id: UtilHelper.generateUniqueVarchar(100),
            modalityCode: coverageRequest.modalityCode,
            firstName: coverageRequest.firstName,
            lastName: coverageRequest.lastName,
            dob: coverageRequest.dob,
            providerNpi: coverageRequest.providerNpi,
            zipCode: coverageRequest.zipCode,
            memberIdNotAvailable: true,
            sessionRate: String(coverageRequest.sessionCharge / 100),
            inNetworkCheck: inNetwork,
            outNetworkCheck: !inNetwork,
            cptCode: coverageRequest.cptCode,
        }
    }

    /**
     * Converts a demo medicaid policy to form input data for the read-only checker.
     */
    private static async toMedicaidFormInputData(
        policy: IOneVerifyDemoMedicaidPolicyDefinition,
    ): Promise<IDynamicCheckerFormInputData> {
        const { coverageRequest } = policy

        return {
            id: UtilHelper.generateUniqueVarchar(100),
            modalityCode: coverageRequest.modalityCode,
            firstName: coverageRequest.firstName,
            lastName: coverageRequest.lastName,
            dob: coverageRequest.dob,
            providerNpi: coverageRequest.providerNpi,
            payer: LookupHelper.getMedicaidDefaultPayer(),
            state: coverageRequest.state,
            memberId: coverageRequest.memberId,
            memberGender: coverageRequest.memberGender,
            cptCode: coverageRequest.cptCode,
            inNetworkCheck: true,
            outNetworkCheck: false,
        }
    }
}
